import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ChevronLeft, Printer } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import NoteRtfEditor from "@/components/encounter/NoteRtfEditor";
import SoapNoteEditor from "@/components/encounter/SoapNoteEditor";
import EncounterCodingPanel from "@/components/encounter/EncounterCodingPanel";
import EncounterNotesList from "@/components/encounter/EncounterNotesList";
import EncounterPatientChart from "@/components/encounter/EncounterPatientChart";
import EncounterReadOnlyHint from "@/components/encounter/EncounterReadOnlyHint";
import EncounterWorkspacePanes from "@/components/encounter/EncounterWorkspacePanes";
import NewNoteDialog from "@/components/encounter/NewNoteDialog";
import NoteHistoryDialog from "@/components/encounter/NoteHistoryDialog";
import NotePrintDocument from "@/components/encounter/NotePrintDocument";
import PatientIdentityBar from "@/components/encounter/PatientIdentityBar";
import { ENC } from "@/constants/testIds";
import "@/styles/emr-encounter-detail.css";
import {
  collapseChart,
  collapseCoding,
  initialChartLayoutState,
  shouldShowActiveNoteChrome,
  showCptForNote,
  showDiagnosisForNote,
} from "@/lib/encounterWorkspaceLayout";
import { useAuth } from "@/context/AuthContext";
import { canWriteMainTab } from "@/lib/maintabAccess";
import { canWriteSubtab } from "@/lib/subtabAccess";
import { fetchEncounter, patientIdOfEncounter, closeEncounter, reopenEncounter, canCloseEncounter, canReopenEncounter, isEncounterReadOnly, ENCOUNTER_READ_ONLY_REASON } from "@/lib/encounterApi";
import { encounterBlocksNote, noteActionState } from "@/lib/noteActionState";
import {
  addLocalDiagnosis,
  applyEncounterDiagnoses,
  clearPrimaryEncounterDiagnosis,
  loadEncounterDiagnoses,
  moveEncounterDiagnosis,
  priorDiagnosesFromEncounters,
  removeEncounterDiagnosis,
  setPrimaryEncounterDiagnosis,
  writeLocalDiagnoses,
} from "@/lib/encounterDiagnoses";
import {
  hydrateCommonDiagnoses,
  hydrateProblemList,
} from "@/lib/diagnosisSources";
import {
  NA,
  chartPatientFields,
  encounterNumber,
  encounterStatusLabel,
  encounterTypeLabel,
  fetchPatient,
  fetchPatientEncounters,
  formatDate,
  resolveIcdDescriptions,
} from "@/lib/patientChart";
import { resolveSoapFields, soapValuesFromContext } from "@/lib/soapFields";
import { readSoapDraft, writeSoapDraft } from "@/lib/soapStorage";
import { chooseSoapEditorText, hydrateVisitSoap, loadVisitSoapContent, persistVisitSoap } from "@/lib/visitNoteSoap";
import { addNoteCpt, fromNoteVersionCpt, persistNoteCpt, removeNoteCpt } from "@/lib/noteCpt";
import {
  chooseWorkingDiagnoses,
  fromNoteVersionDiagnoses,
  hydrateVisitDiagnoses,
  loadVisitNoteDiagnoses,
  persistNoteDiagnoses,
} from "@/lib/visitNoteDiagnoses";
import {
  amendNote,
  canSignNote,
  createNoteIdentity,
  currentVisitNote,
  getNote,
  isVisitNote,
  listEncounterNotes,
  listNoteTypes,
  listNoteVersions,
  noteDisplayStatus,
  noteIsReadOnly,
  persistNoteText,
  shouldShowNotesEmptyState,
  signNote,
  visibleEncounterNotes,
} from "@/lib/noteApi";
import { loadNoteReportContext } from "@/lib/noteReportContext";
import { emptyChartPlaceholderLists, loadChartPlaceholderLists } from "@/lib/patientSummary";
import { buildNotePrintDocument, printNote } from "@/lib/notePrint";

export default function EncounterScreenPage() {
  const { encounterId } = useParams();
  const [searchParams] = useSearchParams();
  const requestedNoteId = searchParams.get("note");
  const { user } = useAuth();
  const canWriteEncounter = canWriteMainTab(user?.maintabAccess, "encounters");
  const canWriteNotes = canWriteSubtab(user?.subtabAccess, "clinical", "notes");
  const [encounter, setEncounter] = useState(null);
  const [patient, setPatient] = useState(null);
  const [descriptionByCode, setDescriptionByCode] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [panes, setPanes] = useState(() =>
    initialChartLayoutState(typeof window === "undefined" ? 1440 : window.innerWidth),
  );
  const [notes, setNotes] = useState([]);
  const [noteTypes, setNoteTypes] = useState([]);
  const [selectedNoteId, setSelectedNoteId] = useState(null);
  const [notesTab, setNotesTab] = useState("note");
  const [otherNoteText, setOtherNoteText] = useState("");
  const [newNoteOpen, setNewNoteOpen] = useState(false);
  const [newNotePreferredType, setNewNotePreferredType] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyVersions, setHistoryVersions] = useState([]);
  const [soapText, setSoapText] = useState("");
  const [signed, setSigned] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [signOpen, setSignOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [loadedId, setLoadedId] = useState(null);
  const [localDiagnoses, setLocalDiagnoses] = useState([]);
  const [localCpt, setLocalCpt] = useState([]);
  const [commonDiagnoses, setCommonDiagnoses] = useState([]);
  const [priorDiagnoses, setPriorDiagnoses] = useState([]);
  const [problemList, setProblemList] = useState([]);
  const documentedRef = useRef(false);
  const [reportContext, setReportContext] = useState(null);
  const [chartLists, setChartLists] = useState(emptyChartPlaceholderLists);

  const load = useCallback(async () => {
    if (!encounterId) return;
    setLoading(true);
    setError(null);
    try {
      const enc = await fetchEncounter(encounterId);
      const patientId = patientIdOfEncounter(enc);
      let pat = null;
      if (patientId) {
        try {
          pat = await fetchPatient(patientId);
        } catch {
          pat = null;
        }
      }
      setEncounter(enc);
      setPatient(pat);
      const encounterLocked = isEncounterReadOnly(enc);
      const fallback = loadEncounterDiagnoses(encounterId, enc);
      const [loadedNotes, loadedTypes, loadedChartLists] = await Promise.all([
        listEncounterNotes(encounterId).catch(() => []),
        listNoteTypes().catch(() => []),
        patientId
          ? loadChartPlaceholderLists(patientId).catch(() => emptyChartPlaceholderLists())
          : emptyChartPlaceholderLists(),
      ]);
      setChartLists(loadedChartLists);
      setNoteTypes(loadedTypes);
      const visit = currentVisitNote(visibleEncounterNotes(loadedNotes));
      const requested = requestedNoteId
        ? loadedNotes.find((note) => note.id === requestedNoteId)
        : null;
      const initial = requested || visit;
      let hydratedNotes = loadedNotes;
      if (initial?.id) {
        try {
          const full = await getNote(initial.id);
          hydratedNotes = loadedNotes.map((note) => (note.id === full.id ? full : note));
        } catch {
          hydratedNotes = loadedNotes;
        }
      }
      setNotes(hydratedNotes);
      setSelectedNoteId(initial?.id || null);
      // Nothing auto-opens unless the encounter has a Visit Note, so land on the
      // list when there are existing notes to pick from.
      const pickable = !initial?.id && visibleEncounterNotes(hydratedNotes).length > 0;
      setNotesTab(pickable ? "list" : "note");
      const opened = hydratedNotes.find((note) => note.id === initial?.id) || initial;
      const noteState = encounterLocked
        ? (await loadVisitNoteDiagnoses(encounterId, visit?.id)) || { diagnoses: [] }
        : await hydrateVisitDiagnoses(encounterId, visit?.id, fallback);
      if (!encounterLocked && (noteState?.diagnoses || []).length) {
        writeLocalDiagnoses(encounterId, noteState.diagnoses);
      }
      const working =
        opened && !isVisitNote(opened)
          ? fromNoteVersionDiagnoses(opened.currentVersion?.diagnoses)
          : chooseWorkingDiagnoses(noteState?.diagnoses, encounterLocked ? [] : fallback);
      setLocalDiagnoses(working);
      const selectedFull = opened || hydratedNotes.find((note) => note.id === visit?.id);
      setLocalCpt(fromNoteVersionCpt(selectedFull?.currentVersion?.cpt));
      const codes = working.map((item) => item.code).filter(Boolean);
      const descriptions = codes.length ? await resolveIcdDescriptions(codes) : {};
      for (const item of working) {
        if (item.code && item.description) descriptions[item.code] = item.description;
      }
      setDescriptionByCode(descriptions);
      documentedRef.current = false;
      const draft = readSoapDraft(encounterId);
      const server = encounterLocked
        ? await loadVisitSoapContent(encounterId, visit?.id)
        : await hydrateVisitSoap(encounterId, visit?.id, draft);
      const soapEditorText = encounterLocked
        ? server?.text || ""
        : chooseSoapEditorText(draft.text, server?.text);
      setSoapText(soapEditorText);
      if (soapEditorText) {
        writeSoapDraft(encounterId, {
          text: soapEditorText,
          resolvedText: server?.resolvedText || draft.resolvedText || "",
          signed: draft.signed,
          savedAt: draft.savedAt,
        });
      }
      setOtherNoteText(
        opened && !isVisitNote(opened) ? opened.currentVersion?.content?.text || "" : "",
      );
      setSigned(noteIsReadOnly(currentVisitNote(hydratedNotes, visit?.id)) || draft.signed);
      setSavedAt(draft.savedAt);
      setLoadedId(encounterId);
    } catch (err) {
      const status = err?.response?.status;
      setEncounter(null);
      setPatient(null);
      setChartLists(emptyChartPlaceholderLists());
      setError(status === 404 ? "Encounter not found." : "Failed to load encounter.");
      if (status !== 404) toast.error("Failed to load encounter.");
    } finally {
      setLoading(false);
    }
  }, [encounterId, requestedNoteId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!encounter) {
      setReportContext(null);
      return undefined;
    }
    let cancelled = false;
    loadNoteReportContext({ encounter, patient }).then((context) => {
      if (!cancelled) setReportContext(context);
    });
    return () => {
      cancelled = true;
    };
  }, [encounter, patient]);

  const fields = useMemo(() => (patient ? chartPatientFields(patient) : null), [patient]);
  const selectedNote = useMemo(
    () => notes.find((note) => note.id === selectedNoteId) || null,
    [notes, selectedNoteId],
  );
  const selectedNoteText = selectedNote
    ? isVisitNote(selectedNote)
      ? soapText
      : otherNoteText
    : "";
  const notePrintDocument = useMemo(
    () =>
      buildNotePrintDocument({
        note: selectedNote,
        fields,
        encounter,
        currentUser: user,
        contentText: selectedNoteText,
        reportTemplate: reportContext?.template,
        reportContext,
      }),
    [selectedNote, fields, encounter, user, selectedNoteText, reportContext],
  );
  const clinicalEncounter = useMemo(
    () => (isVisitNote(selectedNote) ? applyEncounterDiagnoses(encounter, localDiagnoses) : encounter),
    [encounter, localDiagnoses, selectedNote],
  );
  const soapValues = useMemo(
    () =>
      soapValuesFromContext({
        fields,
        encounter: clinicalEncounter,
        descriptionByCode,
        noteDiagnoses: localDiagnoses,
      }),
    [fields, clinicalEncounter, descriptionByCode, localDiagnoses],
  );
  const placeholderContext = useMemo(
    () => ({
      patient,
      fields,
      encounter: clinicalEncounter,
      note: selectedNote,
      diagnoses: localDiagnoses,
      chartLists,
      reportContext,
    }),
    [patient, fields, clinicalEncounter, selectedNote, localDiagnoses, chartLists, reportContext],
  );
  const selectedDiagnosisCodes = useMemo(
    () => localDiagnoses.map((item) => item.code).filter(Boolean),
    [localDiagnoses],
  );

  useEffect(() => {
    const extras = localDiagnoses.map((item) => item.code).filter(Boolean);
    if (!extras.length) return undefined;
    let cancelled = false;
    resolveIcdDescriptions(extras).then((map) => {
      if (cancelled) return;
      setDescriptionByCode((prev) => ({ ...prev, ...map }));
    });
    return () => {
      cancelled = true;
    };
  }, [localDiagnoses]);

  const visitNote = useMemo(() => currentVisitNote(notes, selectedNoteId), [notes, selectedNoteId]);
  const encounterLocked = isEncounterReadOnly(encounter);
  const canWrite = canWriteEncounter && canWriteNotes;
  const noteActions = noteActionState({ encounter, note: selectedNote, canWrite });
  const visitActions = noteActionState({ encounter, note: visitNote, canWrite });
  const visitLocked = visitActions.editorLocked;
  const editorLocked = noteActions.editorLocked;
  const canAmendSelected = noteActions.canAmend;
  const autosaveOff = noteActions.autosaveOff;
  const noteLockedStyle = selectedNote ? noteIsReadOnly(selectedNote) : false;

  const diagnosisOnNote = showDiagnosisForNote(selectedNote);
  const cptOnNote = showCptForNote(selectedNote);
  const noteChrome = shouldShowActiveNoteChrome(selectedNote);

  useEffect(() => {
    if (!diagnosisOnNote) return undefined;
    let cancelled = false;
    if (user?.id) {
      hydrateCommonDiagnoses(user.id).then((items) => {
        if (!cancelled) setCommonDiagnoses(items);
      });
    }
    const patientId = patientIdOfEncounter(encounter);
    if (!patientId) {
      setPriorDiagnoses([]);
      setProblemList([]);
      return () => {
        cancelled = true;
      };
    }
    fetchPatientEncounters(patientId)
      .then(async (encounters) => {
        if (cancelled) return;
        const prior = priorDiagnosesFromEncounters(encounters?.items || encounters || [], encounterId);
        setPriorDiagnoses(prior);
        const problems = await hydrateProblemList(patientId, prior);
        if (!cancelled) setProblemList(problems);
      })
      .catch(async () => {
        if (cancelled) return;
        setPriorDiagnoses([]);
        const problems = await hydrateProblemList(patientId, []);
        if (!cancelled) setProblemList(problems);
      });
    return () => {
      cancelled = true;
    };
  }, [diagnosisOnNote, encounter, encounterId, user?.id]);

  useEffect(() => {
    if (!encounterId || loading || loadedId !== encounterId) return;
    if (encounterBlocksNote(encounter, visitNote)) return;
    const handle = setTimeout(() => {
      const stamp = new Date().toISOString();
      const resolvedText = resolveSoapFields(soapText, soapValues);
      writeSoapDraft(encounterId, {
        text: soapText,
        resolvedText,
        signed,
        savedAt: stamp,
      });
      setSavedAt(stamp);
      if (!documentedRef.current) return;
      if (visitNote && noteIsReadOnly(visitNote)) return;
      persistVisitSoap({
        encounterId,
        text: soapText,
        resolvedText,
        documented: true,
        noteId: visitNote?.id || null,
      })
        .then((result) => {
          if (result?.noteId) {
            getNote(result.noteId)
              .then((full) => {
                setNotes((prev) => {
                  if (prev.some((note) => note.id === full.id)) {
                    return prev.map((note) => (note.id === full.id ? full : note));
                  }
                  return [full, ...prev];
                });
                setSelectedNoteId((current) => current || full.id);
              })
              .catch(() => {});
          }
        })
        .catch(() => {
          /* localStorage remains the fallback; the next debounce retries */
        });
    }, 400);
    return () => clearTimeout(handle);
  }, [encounter, encounterId, soapText, signed, loading, loadedId, soapValues, visitNote]);

  useEffect(() => {
    if (!encounterId || loading || loadedId !== encounterId) return;
    if (!selectedNote || isVisitNote(selectedNote) || noteIsReadOnly(selectedNote) || encounterBlocksNote(encounter, selectedNote)) return;
    if (!String(otherNoteText || "").trim() && !selectedNote.currentVersion) return;
    const handle = setTimeout(() => {
      persistNoteText(selectedNote.id, otherNoteText)
        .then((result) => {
          if (result?.skipped) return;
          return getNote(selectedNote.id).then((full) => {
            setNotes((prev) => prev.map((note) => (note.id === full.id ? full : note)));
          });
        })
        .catch(() => {});
    }, 400);
    return () => clearTimeout(handle);
  }, [encounter, encounterId, loading, loadedId, selectedNote, otherNoteText]);

  if (loading) {
    return (
      <div data-testid={ENC.screen} className="emr-encdetail">
        <h1 className="emr-encdetail__title">Encounter</h1>
        <p className="emr-encdetail__status-msg">Loading encounter…</p>
      </div>
    );
  }

  if (error || !encounter) {
    return (
      <div data-testid={ENC.screen} className="emr-encdetail">
        <section className="card emr-encdetail__section">
          <p className="emr-encdetail__hint">{error || "Encounter not found."}</p>
          <Link to="/encounters" className="emr-encdetail__back">
            <ChevronLeft className="emr-encdetail__back-icon" />
            Back to Encounters
          </Link>
        </section>
      </div>
    );
  }

  const patientId = patientIdOfEncounter(encounter);

  const refreshNote = async (noteId) => {
    const full = await getNote(noteId);
    setNotes((prev) => {
      if (prev.some((note) => note.id === full.id)) {
        return prev.map((note) => (note.id === full.id ? full : note));
      }
      return [full, ...prev];
    });
    return full;
  };

  /** Explicit save. Persists the open Note now instead of waiting for the autosave debounce. */
  const saveNoteNow = async () => {
    if (!selectedNote?.id || editorLocked) return;
    const stamp = new Date().toISOString();
    try {
      if (isVisitNote(selectedNote)) {
        const resolvedText = resolveSoapFields(soapText, soapValues);
        writeSoapDraft(encounterId, { text: soapText, resolvedText, signed, savedAt: stamp });
        documentedRef.current = true;
        const result = await persistVisitSoap({
          encounterId,
          text: soapText,
          resolvedText,
          documented: true,
          noteId: visitNote?.id || selectedNote.id,
        });
        if (result?.noteId) await refreshNote(result.noteId);
      } else {
        const result = await persistNoteText(selectedNote.id, otherNoteText);
        if (!result?.skipped) await refreshNote(selectedNote.id);
      }
      setSavedAt(stamp);
      toast.success("Note saved.");
    } catch {
      toast.error("Could not save the Note.");
    }
  };

  const confirmSign = async () => {
    const target =
      selectedNote && noteDisplayStatus(selectedNote) === "draft"
        ? selectedNote
        : visitNote && noteDisplayStatus(visitNote) === "draft"
          ? visitNote
          : null;
    const signBlockedByEncounter = encounterBlocksNote(encounter, target);
    if (signBlockedByEncounter || !canSignNote(target)) {
      setSignOpen(false);
      toast.info(
        signBlockedByEncounter
          ? ENCOUNTER_READ_ONLY_REASON
          : "Create a draft with SOAP text, diagnoses, or CPT before signing.",
      );
      return;
    }
    try {
      const signedNote = await signNote(target.id);
      setNotes((prev) => prev.map((note) => (note.id === signedNote.id ? signedNote : note)));
      if (isVisitNote(signedNote)) {
        setSigned(true);
        writeSoapDraft(encounterId, {
          text: soapText,
          resolvedText: resolveSoapFields(soapText, soapValues),
          signed: true,
          savedAt: new Date().toISOString(),
        });
      }
      setSignOpen(false);
      toast.success("Note signed. This version is now read-only.");
    } catch {
      toast.error("Could not sign the Note.");
    }
  };

  const confirmCloseEncounter = async () => {
    if (!encounterId || !canCloseEncounter(encounter)) {
      setCloseOpen(false);
      return;
    }
    setClosing(true);
    try {
      const closed = await closeEncounter(encounterId);
      setEncounter(closed);
      setCloseOpen(false);
      toast.success("Encounter completed. Notes were not signed.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not close the encounter.");
    } finally {
      setClosing(false);
    }
  };

  const confirmReopenEncounter = async () => {
    if (!encounterId || !canReopenEncounter(encounter)) {
      setReopenOpen(false);
      return;
    }
    setReopening(true);
    try {
      const reopened = await reopenEncounter(encounterId);
      setEncounter(reopened);
      setReopenOpen(false);
      toast.success("Encounter reopened. Existing notes were not changed.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not reopen the encounter.");
    } finally {
      setReopening(false);
    }
  };

  const onSoapChange = (next) => {
    if (visitLocked) return;
    documentedRef.current = true;
    setSoapText(next);
  };

  const openNewNoteDialog = (preferredTypeCode = null) => {
    if (!canWriteNotes || !canWriteEncounter) return;
    if (isEncounterReadOnly(encounter)) {
      toast.info(ENCOUNTER_READ_ONLY_REASON);
      return;
    }
    setNewNotePreferredType(preferredTypeCode);
    setNewNoteOpen(true);
  };

  const createNewNote = async ({ noteTypeId, title }) => {
    if (isEncounterReadOnly(encounter)) {
      toast.info(ENCOUNTER_READ_ONLY_REASON);
      return;
    }
    try {
      const created = await createNoteIdentity({ encounterId, noteTypeId, title });
      setNotes((prev) => [created, ...prev.filter((note) => note.id !== created.id)]);
      setSelectedNoteId(created.id);
      setNotesTab("note");
      setNewNoteOpen(false);
      if (isVisitNote(created)) {
        setSoapText("");
        documentedRef.current = false;
      } else {
        setOtherNoteText("");
      }
      setLocalCpt([]);
      setLocalDiagnoses([]);
      toast.success("Note created.");
    } catch {
      toast.error("Could not create the Note.");
    }
  };

  const openEncounterNote = async (note) => {
    setSelectedNoteId(note.id);
    setNotesTab("note");
    try {
      const full = await refreshNote(note.id);
      if (isVisitNote(full)) {
        const serverText = full.currentVersion?.content?.text || "";
        if (isEncounterReadOnly(encounter)) {
          setSoapText(serverText);
        } else {
          const draft = readSoapDraft(encounterId);
          const latest = currentVisitNote(notes, full.id);
          setSoapText(
            !latest || latest.id === full.id
              ? chooseSoapEditorText(draft.text, serverText)
              : serverText,
          );
        }
        setSigned(noteIsReadOnly(full));
      } else {
        setOtherNoteText(full.currentVersion?.content?.text || "");
      }
      setLocalDiagnoses(fromNoteVersionDiagnoses(full.currentVersion?.diagnoses));
      setLocalCpt(fromNoteVersionCpt(full.currentVersion?.cpt));
    } catch {
      toast.error("Could not open the Note.");
    }
  };

  /** Addendum is available on a signed Note while the Encounter is open or reopened. */
  const createAmendment = async (kind) => {
    if (!canWrite) return;
    if (kind === "addendum" && isEncounterReadOnly(encounter)) {
      toast.info(ENCOUNTER_READ_ONLY_REASON);
      return;
    }
    const target = selectedNote || visitNote;
    if (!target || noteDisplayStatus(target) !== "signed") {
      toast.info("Addendum and correction are available after the Note is signed.");
      return;
    }
    try {
      await amendNote(target.id, kind);
      const full = await refreshNote(target.id);
      setSelectedNoteId(full.id);
      if (isVisitNote(full)) {
        setSoapText(full.currentVersion?.content?.text || soapText);
        setSigned(false);
      } else {
        setOtherNoteText(full.currentVersion?.content?.text || otherNoteText);
      }
      setLocalDiagnoses(fromNoteVersionDiagnoses(full.currentVersion?.diagnoses));
      setLocalCpt(fromNoteVersionCpt(full.currentVersion?.cpt));
      toast.success(kind === "addendum" ? "Addendum draft created." : "Correction draft created.");
    } catch (error) {
      const status = error?.response?.status;
      const detail = String(error?.response?.data?.detail || "");
      if (status === 409 && detail.toLowerCase().includes("completed")) {
        toast.error(ENCOUNTER_READ_ONLY_REASON);
      } else {
        toast.error(status === 409 ? "A draft version already exists for this Note." : "Could not create the new version.");
      }
    }
  };

  const openHistory = async () => {
    const target = selectedNote || visitNote;
    if (!target) return;
    try {
      const items = await listNoteVersions(target.id);
      setHistoryVersions(items);
      setHistoryOpen(true);
    } catch {
      toast.error("Could not load version history.");
    }
  };

  const persistDiagnoses = (next) => {
    if (!selectedNote?.id || encounterBlocksNote(encounter, selectedNote)) return;
    setLocalDiagnoses(next);
    if (isVisitNote(selectedNote)) {
      writeLocalDiagnoses(encounterId, next);
    }
    persistNoteDiagnoses(selectedNote.id, next)
      .then(async (result) => {
        if (result?.skipped) {
          setLocalDiagnoses(fromNoteVersionDiagnoses(selectedNote.currentVersion?.diagnoses));
          if (result.reason === "signed") {
            toast.info("This Note is signed. Create a correction or addendum to change diagnoses.");
          }
          return;
        }
        try {
          if (isVisitNote(selectedNote)) {
            setEncounter(await fetchEncounter(encounterId));
          }
          await refreshNote(selectedNote.id);
        } catch {
          /* working list remains the display source */
        }
      })
      .catch(() => {
        toast.error("Could not save diagnoses to this Note.");
      });
  };

  const addDiagnosis = (item, source = "lookup") => {
    if (!selectedNote?.id) return;
    if (editorLocked) {
      toast.info(
        noteActions.blockedByEncounter
          ? ENCOUNTER_READ_ONLY_REASON
          : "This Note is signed. Create a correction or addendum to change diagnoses.",
      );
      return;
    }
    if (selectedDiagnosisCodes.includes(item.code)) {
      toast.info(`${item.code} is already on this Note.`);
      return;
    }
    const next = addLocalDiagnosis(localDiagnoses, item, source);
    if (!next.added) {
      toast.info(`${item.code} is already on this Note.`);
      return;
    }
    persistDiagnoses(next.items);
    if (item.description) {
      setDescriptionByCode((prev) => ({ ...prev, [item.code]: item.description }));
    }
    toast.success(`Added ${item.code}${item.description ? ` — ${item.description}` : ""}`);
  };

  const removeDiagnosis = (code) => {
    if (!selectedNote?.id || editorLocked) return;
    persistDiagnoses(removeEncounterDiagnosis(localDiagnoses, code));
  };

  const removeAllDiagnoses = () => {
    if (!selectedNote?.id || editorLocked) return;
    persistDiagnoses([]);
  };

  const makePrimary = (code) => {
    if (!selectedNote?.id || editorLocked) return;
    persistDiagnoses(setPrimaryEncounterDiagnosis(localDiagnoses, code));
  };

  const clearPrimary = () => {
    if (!selectedNote?.id || editorLocked) return;
    persistDiagnoses(clearPrimaryEncounterDiagnosis(localDiagnoses));
  };

  const moveDiagnosis = (code, direction) => {
    if (!selectedNote?.id || editorLocked) return;
    persistDiagnoses(moveEncounterDiagnosis(localDiagnoses, code, direction));
  };

  const persistCpt = (next) => {
    if (!selectedNote?.id || encounterBlocksNote(encounter, selectedNote)) return;
    setLocalCpt(next);
    persistNoteCpt(selectedNote.id, next)
      .then(async (result) => {
        if (result?.skipped) {
          setLocalCpt(fromNoteVersionCpt(selectedNote.currentVersion?.cpt));
          if (result.reason === "signed") {
            toast.info("This Note is signed. Create a correction or addendum to change CPT.");
          }
          return;
        }
        try {
          await refreshNote(selectedNote.id);
        } catch {
          /* working list remains the display source */
        }
      })
      .catch(() => {
        toast.error("Could not save CPT to this Note.");
      });
  };

  const addCpt = (item) => {
    if (!selectedNote?.id) return;
    if (editorLocked) {
      toast.info(
        noteActions.blockedByEncounter
          ? ENCOUNTER_READ_ONLY_REASON
          : "This Note is signed. Create a correction or addendum to change CPT.",
      );
      return;
    }
    const next = addNoteCpt(localCpt, item);
    if (!next.added) {
      toast.info(`${item.code} is already on this Note.`);
      return;
    }
    persistCpt(next.items);
    toast.success(`Added ${item.code}`);
  };

  const removeCpt = (code, system) => {
    if (!selectedNote?.id || editorLocked) return;
    persistCpt(removeNoteCpt(localCpt, code, system));
  };

  const encounterStatusChip =
    encounter.status === "finished"
      ? "emr-encdetail__chip emr-encdetail__chip--finished"
      : encounter.status === "in-progress"
        ? "emr-encdetail__chip emr-encdetail__chip--progress"
        : "emr-encdetail__chip emr-encdetail__chip--neutral";

  return (
    <>
    <div data-testid={ENC.screen} className="emr-encdetail">
      <div className="emr-encdetail__modules">
      <div
        data-testid={ENC.screenTabs}
        className="emr-encdetail__module emr-encdetail__module--wide emr-encdetail__workspace"
        aria-label="Encounter workspace"
      >
        <EncounterWorkspacePanes
          state={panes}
          onStateChange={setPanes}
          chart={
            <EncounterPatientChart
              patientId={patientId}
              fields={fields}
              currentUser={user}
              onCollapse={() => setPanes((current) => collapseChart(current))}
            />
          }
          coding={
            <EncounterCodingPanel
              onCollapse={() => setPanes((current) => collapseCoding(current))}
              diagnosisAvailable={diagnosisOnNote}
              diagnosis={{
                selectedCodes: selectedDiagnosisCodes,
                encounterDiagnoses: localDiagnoses.map((item) => ({
                  ...item,
                  description: descriptionByCode[item.code] || item.description,
                })),
                commonDiagnoses,
                priorDiagnoses,
                problemList,
                onAdd: addDiagnosis,
                onRemove: removeDiagnosis,
                onRemoveAll: removeAllDiagnoses,
                onSetPrimary: makePrimary,
                onClearPrimary: clearPrimary,
                onMove: moveDiagnosis,
                readOnly: editorLocked,
                noteContext: selectedNote
                  ? `${selectedNote.noteType?.displayName || "Visit Note"}${selectedNote.title ? ` “${selectedNote.title}”` : ""} · ${noteDisplayStatus(selectedNote)}`
                  : "the current Visit Note",
              }}
              cptAvailable={cptOnNote}
              cpt={{
                selectedItems: localCpt,
                onAdd: addCpt,
                onRemove: removeCpt,
                readOnly: editorLocked,
                noteContext: selectedNote
                  ? `${selectedNote.noteType?.displayName || "Note"}${selectedNote.title ? ` “${selectedNote.title}”` : ""} · ${noteDisplayStatus(selectedNote)}`
                  : "this Note",
              }}
              orderReadOnly={!canWriteEncounter || encounterLocked}
              emptyHint="Open a Note from the Notes List tab to add diagnoses and CPT codes."
            />
          }
          documentation={
            <>
            <PatientIdentityBar fields={fields} />
            <div data-testid={ENC.screenLifecycle} className="emr-encdetail__encounter-bar">
              <div className="emr-encdetail__encounter-bar-facts">
                <span className="emr-encdetail__encounter-bar-value font-fhir">
                  <span className="emr-encdetail__encounter-bar-label">Encounter #</span>{" "}
                  {encounterNumber(encounter)}
                </span>
                <span className="emr-encdetail__encounter-bar-sep">|</span>
                <span className="emr-encdetail__encounter-bar-value">{soapValues.dos || NA}</span>
                <span className="emr-encdetail__encounter-bar-sep">|</span>
                <span className="emr-encdetail__encounter-bar-value">{encounterTypeLabel(encounter)}</span>
                <span className="emr-encdetail__encounter-bar-sep">|</span>
                <span className={encounterStatusChip}>{encounterStatusLabel(encounter.status)}</span>
              </div>
              <div className="emr-encdetail__encounter-bar-actions">
                {canWriteEncounter && canCloseEncounter(encounter) ? (
                  <button
                    type="button"
                    className="btn emr-encdetail__close"
                    data-testid={ENC.screenClose}
                    onClick={() => setCloseOpen(true)}
                  >
                    Close Encounter
                  </button>
                ) : canWriteEncounter && canReopenEncounter(encounter) ? (
                  <button
                    type="button"
                    className="btn emr-encdetail__reopen"
                    data-testid={ENC.screenReopen}
                    onClick={() => setReopenOpen(true)}
                  >
                    Reopen Encounter
                  </button>
                ) : encounter.status === "planned" ? (
                  <p className="emr-encdetail__hint">
                    Planned. Start Encounter from an Arrived appointment to begin clinical care.
                  </p>
                ) : null}
              </div>
            </div>
            <div
              data-testid={`${ENC.screenTab}-notes`}
              className="emr-encdetail__pane-head emr-encdetail__notes-head"
            >
              <div
                data-testid={ENC.screenNoteTabs}
                className="emr-encdetail__notes-tabs"
                role="tablist"
                aria-label="Notes"
              >
                <button
                  type="button"
                  role="tab"
                  data-testid={ENC.screenNoteTabNote}
                  aria-selected={notesTab === "note"}
                  className={`emr-encdetail__notes-tab${notesTab === "note" ? " emr-encdetail__notes-tab--active" : ""}`}
                  onClick={() => setNotesTab("note")}
                >
                  Note
                </button>
                <button
                  type="button"
                  role="tab"
                  data-testid={ENC.screenNoteTabList}
                  aria-selected={notesTab === "list"}
                  className={`emr-encdetail__notes-tab${notesTab === "list" ? " emr-encdetail__notes-tab--active" : ""}`}
                  onClick={() => setNotesTab("list")}
                >
                  Notes List
                </button>
              </div>
              {canWriteNotes && canWriteEncounter ? (
                <EncounterReadOnlyHint locked={encounterLocked}>
                  <button
                    type="button"
                    className="btn emr-encdetail__primary emr-encdetail__new-note"
                    data-testid={ENC.screenNewNote}
                    disabled={encounterLocked}
                    onClick={() => openNewNoteDialog(null)}
                  >
                    New Note
                  </button>
                </EncounterReadOnlyHint>
              ) : null}
            </div>
            <div className="emr-encdetail__pane-body emr-encdetail__pane-body--notes">
            {shouldShowNotesEmptyState(notes, selectedNote) && notesTab !== "list" ? (
              <div data-testid={ENC.screenNotes} className="emr-encdetail__notes">
                <div data-testid={ENC.screenNotesEmpty} className="emr-encdetail__empty">
                  <p className="emr-encdetail__empty-title">
                    No notes for this encounter
                  </p>
                  <p className="emr-encdetail__empty-copy">
                    {encounterLocked
                      ? ENCOUNTER_READ_ONLY_REASON
                      : canWriteNotes && canWriteEncounter
                        ? "Create a note to begin documentation."
                        : "No notes are available for this encounter."}
                  </p>
                </div>
              </div>
            ) : (
              <div data-testid={ENC.screenNotes} className="emr-encdetail__notes">
                {notesTab === "list" ? (
                  <div
                    data-testid={ENC.screenNoteTabPanelList}
                    className="emr-encdetail__notes-panel emr-encdetail__notes-panel--scroll"
                  >
                    <EncounterNotesList
                      notes={notes}
                      selectedNoteId={selectedNoteId}
                      onSelect={openEncounterNote}
                    />
                  </div>
                ) : noteChrome ? (
                  <div data-testid={ENC.screenNoteTabPanelNote} className="emr-encdetail__notes-panel">
                    <div
                      data-testid={ENC.screenCurrentNote}
                      className="emr-encdetail__current"
                    >
                      <div>
                        <div className="emr-encdetail__current-title">
                          {selectedNote.noteType?.displayName || "Note"}
                          {selectedNote.title ? (
                            <span className="ml-2 font-normal text-slate-500">“{selectedNote.title}”</span>
                          ) : null}
                        </div>
                        <div className="emr-encdetail__current-meta">
                          <span
                            data-testid={ENC.screenNoteStatus}
                            className={
                              noteLockedStyle
                                ? "emr-encdetail__chip emr-encdetail__chip--signed"
                                : "emr-encdetail__chip emr-encdetail__chip--draft"
                            }
                          >
                            {noteDisplayStatus(selectedNote)}
                          </span>
                          {selectedNote.currentVersion?.signedBy ? (
                            <span>
                              Signed by {String(selectedNote.currentVersion.signedBy).slice(0, 8)}
                              {selectedNote.currentVersion.signedAt
                                ? ` · ${formatDate(selectedNote.currentVersion.signedAt, "MMM d, yyyy h:mm a")}`
                                : ""}
                            </span>
                          ) : noteActions.blockedByEncounter ? (
                            <span>{ENCOUNTER_READ_ONLY_REASON}</span>
                          ) : (
                            <span>Unsigned draft — autosave stays on this version</span>
                          )}
                        </div>
                      </div>
                      <div className="emr-encdetail__current-actions">
                        <EncounterReadOnlyHint locked={noteActions.blockedByEncounter}>
                          <button
                            type="button"
                            className="btn emr-encdetail__secondary"
                            data-testid={ENC.screenNoteSave}
                            disabled={editorLocked}
                            onClick={saveNoteNow}
                          >
                            Save Note
                          </button>
                        </EncounterReadOnlyHint>
                        <button
                          type="button"
                          className="btn emr-encdetail__secondary"
                          data-testid={ENC.screenNotePrint}
                          aria-label="Print Note"
                          title="Print Note"
                          disabled={!selectedNote?.id}
                          onClick={() => printNote(notePrintDocument)}
                        >
                          <Printer className="emr-encdetail__icon" />
                          Print
                        </button>
                        <EncounterReadOnlyHint locked={noteActions.blockedByEncounter}>
                          <button
                            type="button"
                            className="btn emr-encdetail__primary"
                            data-testid={ENC.screenNoteSign}
                            disabled={!noteActions.canSign}
                            onClick={() => setSignOpen(true)}
                          >
                            Sign
                          </button>
                        </EncounterReadOnlyHint>
                        <EncounterReadOnlyHint locked={encounterLocked && !canAmendSelected}>
                          <button
                            type="button"
                            className="btn emr-encdetail__secondary"
                            data-testid={ENC.screenNoteAddendum}
                            disabled={!canAmendSelected}
                            onClick={() => createAmendment("addendum")}
                          >
                            Add Addendum
                          </button>
                        </EncounterReadOnlyHint>
                        <button
                          type="button"
                          className="btn emr-encdetail__secondary"
                          data-testid={ENC.screenNoteHistory}
                          onClick={openHistory}
                        >
                          View History
                        </button>
                      </div>
                    </div>

                    <div className="emr-encdetail__notes-body">
                    <div className="emr-encdetail__notes-main">
                    <div className="emr-encdetail__editor-wrap">
                    {isVisitNote(selectedNote) ? (
                      <SoapNoteEditor
                        value={soapText}
                        onChange={onSoapChange}
                        readOnly={visitLocked}
                        lockLabel={visitActions.blockedByEncounter ? ENCOUNTER_READ_ONLY_REASON : undefined}
                        patient={patient}
                        placeholderContext={placeholderContext}
                      />
                    ) : (
                      <NoteRtfEditor
                        value={otherNoteText}
                        onChange={setOtherNoteText}
                        readOnly={editorLocked}
                        testId={ENC.screenNoteEditor}
                        patient={patient}
                        placeholderContext={placeholderContext}
                        ariaLabel={selectedNote.noteType?.displayName || "Note"}
                        placeholder={
                          editorLocked
                            ? ""
                            : `Write the ${selectedNote.noteType?.displayName || "note"}…`
                        }
                        className={`emr-encdetail__editor emr-encdetail__editor--rtf ${
                          editorLocked ? "emr-encdetail__editor--locked" : ""
                        }`}
                      />
                    )}
                    </div>
                    </div>
                    </div>
                  </div>
                ) : (
                  <div className="emr-encdetail__notes-panel emr-encdetail__notes-panel--empty">
                    <p className="emr-encdetail__hint">
                      Open a Note from the Notes List tab to start writing.
                    </p>
                  </div>
                )}
              </div>
            )}
            </div>
            </>
          }
        />
      </div>
      </div>

      <div className="emr-encdetail__autosave">
        <span className="inline-flex items-center gap-1.5">
          <span className={`emr-encdetail__autosave-dot${autosaveOff ? " emr-encdetail__autosave-dot--off" : ""}`} />
          Auto-save: {autosaveOff ? "Off" : "On"}
        </span>
        <span>
          Last saved:{" "}
          {savedAt ? formatDate(savedAt, "h:mm a") : "Not yet"}
        </span>
      </div>

      <AlertDialog open={signOpen} onOpenChange={setSignOpen}>
        <AlertDialogContent data-testid={ENC.screenSignDialog} className="emr-encdetail-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Sign this Note?</AlertDialogTitle>
            <AlertDialogDescription>
              Signing locks the current draft version. SOAP and diagnoses on this version become
              read-only. Use Addendum or Correction to change a signed Note. There is no Unsign.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid={ENC.screenConfirmSign}
              className="bg-[#0b1f33] hover:bg-[#132a44]"
              onClick={confirmSign}
            >
              Sign Note
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={closeOpen} onOpenChange={setCloseOpen}>
        <AlertDialogContent data-testid={ENC.screenCloseDialog} className="emr-encdetail-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Close Encounter?</AlertDialogTitle>
            <AlertDialogDescription>
              This will mark the encounter as completed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={closing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid={ENC.screenConfirmClose}
              className="bg-[#0d5c4d] hover:bg-[#0a4b3f]"
              disabled={closing}
              onClick={(event) => {
                event.preventDefault();
                confirmCloseEncounter();
              }}
            >
              {closing ? "Closing…" : "Close Encounter"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={reopenOpen} onOpenChange={setReopenOpen}>
        <AlertDialogContent data-testid={ENC.screenReopenDialog} className="emr-encdetail-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Reopen Encounter?</AlertDialogTitle>
            <AlertDialogDescription>
              This will allow additional clinical documentation for this encounter.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reopening}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid={ENC.screenConfirmReopen}
              className="bg-[#0d5c4d] hover:bg-[#0a4b3f]"
              disabled={reopening}
              onClick={(event) => {
                event.preventDefault();
                confirmReopenEncounter();
              }}
            >
              {reopening ? "Reopening…" : "Reopen Encounter"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <NewNoteDialog
        open={newNoteOpen}
        onOpenChange={setNewNoteOpen}
        noteTypes={noteTypes}
        preferredTypeCode={newNotePreferredType}
        locked={encounterLocked}
        encounterId={encounterId}
        onCreate={createNewNote}
      />
      <NoteHistoryDialog
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        note={selectedNote}
        versions={historyVersions}
      />

    </div>
    <NotePrintDocument document={notePrintDocument} />
    </>
  );
}
